/*
 * Exceedios / Calluve Custom Cart Drawer for Horizon
 * ---------------------------------------------------
 * A dependency-free (no jQuery, no Slick) re-implementation of the original
 * Dawn-era "adev" slide cart, wired natively into Shopify Horizon.
 *
 * Features: slide-out drawer, free-shipping progress bar, in-cart upsells
 * (carousel or list) with a variant-select popup, shipping-protection toggle
 * (+ optional auto-add), quantity controls, trust badge, and full Section
 * Rendering API refresh. Keeps Horizon's header cart bubble in sync by
 * dispatching the theme's native `cart:update` event.
 */
(function () {
  'use strict';

  var SECTION = 'adev-cart-drawer';
  var STORAGE_SP = 'adev_shipping_protection';

  function cfg() { return window.adevCartConfig || {}; }
  function routes() {
    var r = (window.Theme && window.Theme.routes) || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      change: r.cart_change_url || '/cart/change.js',
      update: r.cart_update_url || '/cart/update.js',
      cart: r.cart_url || '/cart'
    };
  }
  function isDesignMode() { return !!(window.Shopify && window.Shopify.designMode); }

  /* ------------------------------------------------------------------ utils */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }

  /* localStorage with expiration (ported from the original adev framework) */
  function setItemWithExpiration(key, value, ttl) {
    try {
      localStorage.setItem(key, JSON.stringify({ value: value, expiration: ttl ? Date.now() + ttl : null }));
    } catch (e) {}
  }
  function getItemWithExpiration(key) {
    try {
      var item = JSON.parse(localStorage.getItem(key));
      if (!item) return null;
      if (item.expiration && Date.now() > item.expiration) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value;
    } catch (e) { return null; }
  }

  /* Shopify-compatible money formatter */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || cfg().moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    var value = '';
    var match = fmt.match(placeholder);
    switch (match && match[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  /* Shopify.Image.getSizedImageUrl replica (for the upsell variant popup) */
  function getSizedImageUrl(src, size) {
    if (!src) return src;
    if (size === 'master') return src.replace(/http(s)?:/, '');
    var match = src.match(/\.(jpg|jpeg|gif|png|bmp|bmp\?v=\d+|webp)(\?v=\d+)?$/i);
    if (!match) return src;
    var prefix = src.split(match[0]);
    var suffix = match[0];
    return prefix[0] + '_' + size + suffix;
  }

  /* ---------------------------------------------------------------- drawer */
  var html = document.documentElement;
  var upsellSlideIndex = 0;
  var scrollPosition = 0;

  function drawerEl() { return qs('[data-adev-drawer="cart-drawer"]'); }
  function overlayEl() { return qs('[data-adev-drawer-overlay]'); }
  function contentsEl() { return qs('[data-adev-cart-drawer-contents]'); }

  function updateFooterVar() {
    var footer = qs('[data-adev-cart-drawer-footer]');
    html.style.setProperty('--adev-cart-drawer-footer-height', (footer ? footer.offsetHeight : 191) + 'px');
  }

  function openDrawer() {
    var d = drawerEl();
    if (!d) return;
    d.classList.add('adev-opened');
    d.setAttribute('tabindex', '0');
    var o = overlayEl();
    if (o) o.classList.add('adev-opened');
    html.classList.add('adev-noscroll');
    updateFooterVar();
    try { d.focus({ preventScroll: true }); } catch (e) {}
  }

  function closeDrawer() {
    var d = drawerEl();
    if (d) { d.classList.remove('adev-opened'); d.setAttribute('tabindex', '-1'); }
    var o = overlayEl();
    if (o) o.classList.remove('adev-opened');
    html.classList.remove('adev-noscroll');
    closeUpsellPopup();
  }

  function setLoading(state) {
    var c = contentsEl();
    if (c) c.classList.toggle('loading', !!state);
  }

  /* preserve scroll position of the items list across refreshes */
  function rememberScroll() {
    var el = qs('.adev-drawer__full-scroll .adev-cart-drawer__inner') || qs('.adev-drawer__limited-scroll .adev-cart-drawer__items');
    scrollPosition = el ? el.scrollTop : 0;
  }
  function restoreScroll() {
    var el = qs('.adev-drawer__full-scroll .adev-cart-drawer__inner') || qs('.adev-drawer__limited-scroll .adev-cart-drawer__items');
    if (el) el.scrollTop = scrollPosition;
  }

  /* ------------------------------------------------------------ cart sync */
  function cartFetch(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Adev': '1' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  /* Universal cart-mutation interceptor.
     Some product forms (e.g. Dropmagic) add to cart without dispatching the
     theme's `cart:update` event. We intercept fetch + XHR to `/cart/*` so the
     drawer opens and stays in sync no matter what triggered the change. */
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var externalRefresh = debounce(function (shouldOpen) {
    fetchSection().then(function (h) {
      swapContents(h);
      broadcastCartUpdate();
      if (shouldOpen && cfg().autoOpen !== false) openDrawer();
      maybeAutoAddShippingProtection();
    });
  }, 250);
  function scheduleExternalRefresh(isAdd) { externalRefresh(isAdd); }

  function installCartInterceptors() {
    if (window.__adevCartIntercept) return;
    window.__adevCartIntercept = true;

    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var mine = init && init.headers && (init.headers['X-Adev'] || init.headers['x-adev']);
        var p = nativeFetch.apply(this, arguments);
        if (!mine && CART_MUTATION_RE.test(url)) {
          p.then(function (res) { if (res && res.ok) scheduleExternalRefresh(/\/cart\/add/.test(url)); }).catch(function () {});
        }
        return p;
      };
    }

    try {
      var XO = XMLHttpRequest.prototype.open, XS = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) { this.__adevUrl = url || ''; return XO.apply(this, arguments); };
      XMLHttpRequest.prototype.send = function () {
        var xhr = this, url = xhr.__adevUrl || '';
        if (CART_MUTATION_RE.test(url)) {
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) scheduleExternalRefresh(/\/cart\/add/.test(url));
          });
        }
        return XS.apply(this, arguments);
      };
    } catch (e) {}
  }

  function currentCartCount() {
    var el = qs('[data-adev-cart-count]');
    return el ? parseInt(el.textContent, 10) || 0 : 0;
  }

  /* Tell Horizon's <cart-icon> (and anything else) the cart changed. */
  function broadcastCartUpdate() {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: {}, sourceId: 'adev-cart', data: { source: 'adev-cart', itemCount: currentCartCount() } }
    }));
  }

  function swapContents(htmlString) {
    if (htmlString == null) return;
    var doc = new DOMParser().parseFromString(htmlString, 'text/html');
    var fresh = doc.querySelector('[data-adev-cart-drawer-contents]');
    var current = contentsEl();
    if (fresh && current) {
      rememberScroll();
      current.innerHTML = fresh.innerHTML;
      restoreScroll();
      initUpsellCarousels();
      updateFooterVar();
    }
    setLoading(false);
  }

  /* Fetch our section fresh (used when the change happened outside the drawer).
     Uses the Section Rendering API on a normal page path — NOT `/cart`, which
     returns the cart JSON object instead of rendered section HTML. */
  function fetchSection() {
    return fetch('/?sections=' + SECTION + '&t=' + Date.now())
      .then(function (r) { return r.json(); })
      .then(function (json) { return json[SECTION]; });
  }

  /* After any drawer-initiated mutation: re-render, sync bubble, maybe auto-add SP */
  function afterMutation(response) {
    var sections = response && response.sections;
    var htmlString = sections && sections[SECTION];
    if (htmlString != null) {
      swapContents(htmlString);
      broadcastCartUpdate();
      maybeAutoAddShippingProtection();
    } else {
      fetchSection().then(function (h) { swapContents(h); broadcastCartUpdate(); maybeAutoAddShippingProtection(); });
    }
  }

  function onError(err) {
    setLoading(false);
    if (window.console) console.error('[adev-cart]', err);
  }

  /* ------------------------------------------------ shipping protection auto-add */
  function maybeAutoAddShippingProtection() {
    var sp = cfg().shippingProtection || {};
    if (!sp.enabled || !sp.autoAdd || !sp.productId) return;
    if (isDesignMode()) return;
    if (getItemWithExpiration(STORAGE_SP) === 'true') return;
    // Only auto-add if not already present in the cart
    var already = qs('[data-adev-shipping-protection-atc].active');
    if (already) return;
    setItemWithExpiration(STORAGE_SP, 'true', 172800000); // 48h
    cartFetch(routes().add, { id: sp.productId, quantity: 1, sections: SECTION, sections_url: location.pathname })
      .then(function (resp) {
        if (resp && resp.sections && resp.sections[SECTION] != null) {
          swapContents(resp.sections[SECTION]);
          broadcastCartUpdate();
        }
      })
      .catch(onError);
  }

  /* ------------------------------------------------------- quantity controls */
  function stepQuantity(input, dir) {
    var max = parseInt(input.getAttribute('max'), 10);
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 0;
    var val = parseInt(input.value, 10);
    if (isNaN(val)) val = 0;
    if (dir > 0) {
      if (!isNaN(max) && val >= max) return;
      val += 1;
    } else {
      if (val <= min) { val = min; }
      else { val -= 1; }
    }
    input.value = val;
    input.setAttribute('value', val);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  var onFormChange = debounce(function () {
    var form = qs('[data-adev-cart-drawer-form]');
    if (!form) return;
    setLoading(true);
    var updates = {};
    qsa('input[name^="updates["]', form).forEach(function (inp) {
      var m = inp.name.match(/updates\[(.+)\]/);
      if (m) updates[m[1]] = parseInt(inp.value, 10) || 0;
    });
    cartFetch(routes().update, { updates: updates, sections: SECTION, sections_url: location.pathname })
      .then(afterMutation)
      .catch(onError);
  }, 400);

  /* ------------------------------------------------------------- upsell carousel */
  function initUpsellCarousels() {
    qsa('[data-adev-cart-drawer-upsell="true"]').forEach(function (track) {
      if (track.classList.contains('adev-carousel-initialized')) return;
      track.classList.add('adev-carousel-initialized', 'adev-carousel');

      var wrap = track.parentNode;
      wrap.classList.add('adev-upsell__carousel');

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'slick-arrow slick-prev-arrow';
      prev.setAttribute('aria-label', 'Previous');
      prev.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"><g stroke-width="1.5" fill="none"><path d="M15 6l-6 6l6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></g></svg>';

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'slick-arrow slick-next-arrow';
      next.setAttribute('aria-label', 'Next');
      next.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"><g stroke-width="1.5" fill="none"><path d="M9 6l6 6l-6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></g></svg>';

      wrap.insertBefore(prev, track);
      wrap.appendChild(next);

      var slides = qsa('.adev-upsell-item-col', track);
      var index = Math.min(upsellSlideIndex, Math.max(0, slides.length - 1));

      function slideWidth() { return slides.length ? slides[0].getBoundingClientRect().width : track.clientWidth; }
      function go(i) {
        index = Math.max(0, Math.min(i, slides.length - 1));
        upsellSlideIndex = index;
        track.scrollTo({ left: slideWidth() * index, behavior: 'smooth' });
        updateArrows();
      }
      function updateArrows() {
        prev.classList.toggle('slick-disabled', index <= 0);
        next.classList.toggle('slick-disabled', index >= slides.length - 1);
      }

      prev.addEventListener('click', function () { go(index - 1); });
      next.addEventListener('click', function () { go(index + 1); });
      track.addEventListener('scroll', debounce(function () {
        var w = slideWidth();
        if (w) { index = Math.round(track.scrollLeft / w); upsellSlideIndex = index; updateArrows(); }
      }, 80));

      if (slides.length <= 1) { prev.style.display = 'none'; next.style.display = 'none'; }
      // restore remembered slide without animation
      track.scrollLeft = slideWidth() * index;
      updateArrows();
    });
  }

  /* ----------------------------------------------------------- upsell popup */
  var productData = {};

  function openUpsellPopup(id) {
    var popup = qs('[data-adev-upsell-popup]');
    var product = qs('[data-uspp-upsell-product="' + id + '"]');
    if (!popup || !product) return;
    qsa('[data-uspp-upsell-product].active').forEach(function (p) { p.classList.remove('active'); });
    popup.classList.add('active');
    product.classList.add('active');
  }
  function closeUpsellPopup() {
    qsa('[data-adev-upsell-popup].active').forEach(function (p) { p.classList.remove('active'); });
    qsa('[data-uspp-upsell-product].active').forEach(function (p) { p.classList.remove('active'); });
  }

  function primeUpsellData() {
    qsa('[data-adev-upsell-popup]:not(.adev-initialized)').forEach(function (popup) {
      popup.classList.add('adev-initialized');
      qsa('[data-uspp-upsell-product]', popup).forEach(function (productEl) {
        var script = qs('script', productEl);
        if (!script) return;
        var data;
        try { data = JSON.parse(script.textContent); } catch (e) { return; }
        productData[data.id] = data;
      });
    });
  }

  function updateUpsellVariant(productEl, data) {
    var pricesEl = qs('[data-uspp-product-prices]', productEl);
    var addBtn = qs('[data-uspp-add-to-cart]', productEl);
    var regularPrice = qs('[data-uspp-regular-price]', productEl);
    var compareAtPrice = qs('[data-uspp-compare-at-price]', productEl);
    var selected = qsa('[data-uspp-variant-option-input]', productEl).map(function (s) { return s.value; });
    var variant = (data.variants || []).find(function (v) {
      return v.options.every(function (opt, i) { return selected[i] === opt; });
    });
    if (!variant) return;
    data.current_variant = variant;
    var titleEl = qs('[data-uspp-variant-title]', productEl);
    if (titleEl) titleEl.textContent = variant.title;
    if (addBtn) {
      addBtn.setAttribute('href', '/cart/add/' + variant.id);
      addBtn.setAttribute('data-adev-product-id', variant.id);
      var span = qs('span', addBtn);
      if (variant.available) { addBtn.classList.remove('disabled'); if (span) span.textContent = 'Add to Cart'; }
      else { addBtn.classList.add('disabled'); if (span) span.textContent = 'Sold Out'; }
    }
    if (variant.compare_at_price > variant.price) {
      if (pricesEl) pricesEl.setAttribute('data-on-sale', 'true');
      if (regularPrice) regularPrice.innerHTML = formatMoney(variant.price);
      if (compareAtPrice) { compareAtPrice.style.display = ''; var s = qs('s', compareAtPrice); if (s) s.innerHTML = formatMoney(variant.compare_at_price); }
    } else {
      if (pricesEl) pricesEl.setAttribute('data-on-sale', 'false');
      if (regularPrice) regularPrice.innerHTML = formatMoney(variant.price);
      if (compareAtPrice) { compareAtPrice.style.display = 'none'; var s2 = qs('s', compareAtPrice); if (s2) s2.innerHTML = ''; }
    }
    if (variant.featured_image && variant.featured_image.src) {
      var img = qs('img', productEl);
      if (img) img.setAttribute('src', getSizedImageUrl(variant.featured_image.src, '100x'));
    }
  }

  /* --------------------------------------------------------- event wiring */
  function bind() {
    // Open / close
    document.addEventListener('click', function (e) {
      var opener = closest(e.target, '[data-adev-open-drawer]');
      if (opener) { e.preventDefault(); openDrawer(); return; }

      if (closest(e.target, '[data-adev-close-drawer]')) { e.preventDefault(); closeDrawer(); return; }

      var overlay = e.target.matches && e.target.matches('[data-adev-drawer-overlay]') ? e.target : null;
      if (overlay) { e.preventDefault(); closeDrawer(); return; }

      // Quantity +/-
      var qUp = closest(e.target, '[data-adev-quantity-up]');
      if (qUp) { e.preventDefault(); var iU = qs('[data-adev-quantity-value]', qUp.parentNode); if (iU) stepQuantity(iU, 1); return; }
      var qDown = closest(e.target, '[data-adev-quantity-down]');
      if (qDown) { e.preventDefault(); var iD = qs('[data-adev-quantity-value]', qDown.parentNode); if (iD) stepQuantity(iD, -1); return; }

      // Remove line item
      var remove = closest(e.target, '[data-adev-remove-cart-drawer-item]');
      if (remove) {
        e.preventDefault();
        setLoading(true);
        cartFetch(routes().change, { id: remove.getAttribute('data-key'), quantity: 0, sections: SECTION, sections_url: location.pathname })
          .then(afterMutation).catch(onError);
        return;
      }

      // Upsell: open variant popup
      var upsellAdd = closest(e.target, '[data-adev-upsell-add]');
      if (upsellAdd) { e.preventDefault(); openUpsellPopup(upsellAdd.getAttribute('data-adev-upsell-add')); return; }

      // Upsell popup: click outside closes
      if (e.target.matches && e.target.matches('[data-adev-upsell-popup]')) { closeUpsellPopup(); return; }

      // Upsell popup: add to cart
      var uspp = closest(e.target, '[data-uspp-add-to-cart]');
      if (uspp) {
        e.preventDefault();
        if (uspp.classList.contains('disabled')) return;
        var variantId = uspp.getAttribute('data-adev-product-id');
        var span = qs('span', uspp);
        uspp.classList.add('loading');
        if (span) span.textContent = 'Adding...';
        cartFetch(routes().add, { id: variantId, quantity: 1, sections: SECTION, sections_url: location.pathname })
          .then(function (resp) {
            closeUpsellPopup();
            uspp.classList.remove('loading');
            if (span) span.textContent = 'Add to Cart';
            afterMutation(resp);
          })
          .catch(function (err) { uspp.classList.remove('loading'); if (span) span.textContent = 'Add to Cart'; onError(err); });
        return;
      }

      // Shipping protection toggle
      var sp = closest(e.target, '[data-adev-shipping-protection-atc]');
      if (sp) {
        e.preventDefault();
        setLoading(true);
        var spId = sp.getAttribute('data-id');
        var isActive = sp.classList.contains('active');
        sp.classList.toggle('active');
        var payload = isActive
          ? { id: spId, quantity: 0, sections: SECTION, sections_url: location.pathname }
          : { id: spId, quantity: 1, sections: SECTION, sections_url: location.pathname };
        cartFetch(isActive ? routes().change : routes().add, payload)
          .then(function (resp) {
            var sections = resp && resp.sections;
            if (sections && sections[SECTION] != null) { swapContents(sections[SECTION]); broadcastCartUpdate(); }
            else { fetchSection().then(function (h) { swapContents(h); broadcastCartUpdate(); }); }
          })
          .catch(onError);
        return;
      }
    });

    // Quantity input change → debounced bulk update
    document.addEventListener('change', function (e) {
      if (closest(e.target, '[data-adev-cart-drawer-form]') && e.target.matches('input')) {
        setLoading(true);
        onFormChange();
      }
    });

    // Upsell variant select change
    document.addEventListener('change', function (e) {
      var sel = closest(e.target, '[data-uspp-variant-option-input]');
      if (!sel) return;
      var productEl = closest(sel, '[data-uspp-upsell-product]');
      if (!productEl) return;
      var idEl = productEl.getAttribute('data-uspp-upsell-product');
      var data = productData[idEl];
      if (data) updateUpsellVariant(productEl, data);
    });

    // Escape closes the drawer / popup
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var popup = qs('[data-adev-upsell-popup].active');
      if (popup) { closeUpsellPopup(); return; }
      var d = drawerEl();
      if (d && d.classList.contains('adev-opened')) closeDrawer();
    });

    // Keep drawer in sync with cart changes that happen elsewhere (PDP add, quick-add…)
    document.addEventListener('cart:update', function (e) {
      var detail = e.detail || {};
      var data = detail.data || {};
      if (data.source === 'adev-cart') return; // our own broadcast
      scheduleExternalRefresh(data.source === 'product-form-component');
    });

    // Recalculate footer height on resize
    window.addEventListener('resize', debounce(updateFooterVar, 150));

    // Theme editor: reinit when the section reloads / selects
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-adev-drawer="cart-drawer"]')) init();
    });
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-adev-drawer="cart-drawer"]')) openDrawer();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-adev-drawer="cart-drawer"]')) closeDrawer();
    });
  }

  /* ------------------------------------------------------------------ init */
  var bound = false;
  function init() {
    installCartInterceptors();
    primeUpsellData();
    initUpsellCarousels();
    updateFooterVar();
    if (!bound) { bind(); bound = true; }
  }

  // Install interceptors immediately too, so adds that happen before
  // DOMContentLoaded are still caught.
  installCartInterceptors();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // expose a tiny API (handy for debugging / other scripts)
  window.adevCart = { open: openDrawer, close: closeDrawer, refresh: function () { return fetchSection().then(swapContents); } };
})();
